import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaExclamationTriangle, FaKey, FaCog } from 'react-icons/fa';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import { Location } from '../hooks/useLocationsData';

interface MapContainerProps {
  selectedLocation?: Location | null;
  className?: string;
}

export const MapContainer: React.FC<MapContainerProps> = ({ 
  selectedLocation, 
  className = '' 
}) => {
  const { 
    mapRef, 
    isLoaded,
    isLoading,
    error, 
    showLocation, 
    resetMapView 
  } = useGoogleMaps();

  // Mostrar ubicación cuando se seleccione una nueva
  useEffect(() => {
    if (selectedLocation && isLoaded) {
      showLocation(selectedLocation);
    } else if (!selectedLocation && isLoaded) {
      resetMapView();
    }
  }, [selectedLocation?.id, isLoaded]);

  // Estado de carga inicial
  if (isLoading) {
    return (
      <div className={`bg-white/20 backdrop-blur-xl rounded-2xl shadow-2xl flex items-center justify-center ${className}`}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center p-8"
        >
          <div className="relative mb-6">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
            <FaMapMarkerAlt className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-primary text-xl" />
          </div>
          <h3 className="text-lg font-bold text-secondary mb-2">Cargando Google Maps</h3>
          <p className="text-text-light text-sm">Esto puede tomar unos segundos...</p>
          <div className="mt-4 flex justify-center space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Estado de error con diferentes tipos de mensajes
  if (error) {
    const isAPIKeyError = error.includes('API Key') || error.includes('InvalidKey');
    const isQuotaError = error.includes('cuota') || error.includes('Quota');
    const isPermissionError = error.includes('denegada') || error.includes('Denied');
    
    return (
      <div className={`bg-white/20 backdrop-blur-xl rounded-2xl shadow-2xl flex items-center justify-center ${className}`}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center p-8 max-w-md"
        >
          {/* Icono específico según el tipo de error */}
          {isAPIKeyError && <FaKey className="text-red-400 text-5xl mx-auto mb-4" />}
          {isQuotaError && <FaCog className="text-yellow-400 text-5xl mx-auto mb-4" />}
          {isPermissionError && <FaExclamationTriangle className="text-orange-400 text-5xl mx-auto mb-4" />}
          {!isAPIKeyError && !isQuotaError && !isPermissionError && <FaExclamationTriangle className="text-red-400 text-5xl mx-auto mb-4" />}
          
          <h3 className="text-xl font-bold text-red-400 mb-4">Error cargando el mapa</h3>
          <p className="text-text-light text-sm mb-6 leading-relaxed">{error}</p>
          
          {/* Soluciones específicas */}
          <div className="bg-red-100/10 rounded-lg p-4 text-left">
            <h4 className="font-semibold text-red-300 mb-3 text-sm">💡 Posibles soluciones:</h4>
            <ul className="text-xs text-red-300 space-y-2 list-disc list-inside">
              {isAPIKeyError && (
                <>
                  <li>Verifica que el archivo .env contiene REACT_APP_GOOGLE_MAPS_API_KEY</li>
                  <li>Asegúrate de que la API key sea correcta y esté activa</li>
                  <li>Habilita "Maps JavaScript API" en Google Cloud Console</li>
                </>
              )}
              {isQuotaError && (
                <>
                  <li>Verifica tu facturación en Google Cloud Console</li>
                  <li>Revisa si has excedido los límites de uso gratuito</li>
                </>
              )}
              {isPermissionError && (
                <>
                  <li>Configura las restricciones de dominio en Google Cloud Console</li>
                  <li>Permite localhost para desarrollo</li>
                </>
              )}
              <li>Reinicia el servidor de desarrollo (npm start)</li>
              <li>Revisa la consola del navegador para más detalles</li>
            </ul>
          </div>
          
          {/* Enlace a la documentación */}
          <div className="mt-4">
            <a
              href="https://developers.google.com/maps/documentation/javascript/get-api-key"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-sm transition-colors"
            >
              <FaKey className="text-xs" />
              Obtener API Key
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  // Mapa cargado exitosamente
  return (
    <div className={`bg-white/20 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden ${className}`}>
      {/* Header del mapa */}
      <div className="p-4 border-b border-white/20 bg-white/10">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-secondary flex items-center gap-2">
            <FaMapMarkerAlt className="text-red" />
            {selectedLocation ? selectedLocation.name : 'Mapa'}
          </h3>
          {selectedLocation && (
            <button
              onClick={resetMapView}
              className="px-3 py-1 bg-primary/20 hover:bg-primary/30 text-secondary rounded-lg text-sm font-semibold transition-colors"
            >
              Ver Todo
            </button>
          )}
        </div>
        {selectedLocation && (
          <p className="text-sm text-text-light mt-1">{selectedLocation.description}</p>
        )}
      </div>

      {/* Contenedor del mapa */}
      <div className="relative">
        <div
          ref={mapRef}
          className="w-full h-[550px] bg-gray-200"
          style={{ minHeight: '500px' }}
        />
        
        {/* Overlay cuando no hay ubicación seleccionada */}
        {!selectedLocation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/40 flex items-center justify-center pointer-events-none"
          >
            <div className="text-center text-white">
              <FaMapMarkerAlt className="text-4xl mx-auto mb-4 opacity-60" />
              <h4 className="text-xl font-bold mb-2">Selecciona una ubicación</h4>
              <p className="text-sm opacity-80">Elige un lugar de la lista para verlo en el mapa</p>
            </div>
          </motion.div>
        )}

        {/* Indicador de ubicación actual mejorado */}
        {selectedLocation && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg max-w-xs border border-white/20"
          >
            <div className="flex items-start gap-3">
              <FaMapMarkerAlt className="text-primary mt-1 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h5 className="font-bold text-gray-800 text-sm truncate">{selectedLocation.name}</h5>
                {selectedLocation.address && (
                  <p className="text-xs text-gray-600 mt-1 line-clamp-2">{selectedLocation.address}</p>
                )}
                {selectedLocation.notes && (
                  <p className="text-xs text-gray-500 mt-1 italic line-clamp-2">"{selectedLocation.notes}"</p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Indicador de estado de carga del mapa */}
        {isLoaded && (
          <div className="absolute top-2 right-2 bg-green-500/20 backdrop-blur-sm rounded-full p-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          </div>
        )}
      </div>
    </div>
  );
};